
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { BlogPostFormData } from '@/types/blog';

interface UseAutosaveProps {
  id: string;
  formData: BlogPostFormData;
  hasUnsavedChanges: boolean;
  setLastSaved: (date: Date | null) => void;
  setIsSaving: (isSaving: boolean) => void;
}

/**
 * Custom hook to handle auto-saving blog post data to localStorage
 */
export const useAutosave = ({
  id,
  formData,
  hasUnsavedChanges,
  setLastSaved,
  setIsSaving
}: UseAutosaveProps) => {
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const AUTO_SAVE_INTERVAL = 30000; // 30 seconds

  // Get localStorage key for auto-save
  const getLocalStorageKey = useCallback((postId: string) => `blog_post_draft_${postId}`, []);

  // Save data to localStorage
  const saveToLocalStorage = useCallback(() => {
    if (!id) return;
    
    try {
      setIsSaving(true);
      const storageKey = getLocalStorageKey(id);
      localStorage.setItem(storageKey, JSON.stringify(formData));
      setLastSaved(new Date());
      console.log("Auto-saved to localStorage", formData);
    } catch (error) {
      console.error("Error auto-saving:", error);
    } finally {
      setIsSaving(false);
    }
  }, [formData, id, getLocalStorageKey, setIsSaving, setLastSaved]);

  // Clear localStorage data
  const clearLocalStorage = useCallback(() => {
    if (!id) return;
    
    try {
      localStorage.removeItem(getLocalStorageKey(id));
      console.log("Cleared localStorage draft data");
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  }, [id, getLocalStorageKey]);

  // Load from localStorage
  const loadFromLocalStorage = useCallback(() => {
    if (!id) return null;
    
    try {
      const savedData = localStorage.getItem(getLocalStorageKey(id));
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        
        // If we have scheduled_for as a string, convert it to Date
        if (parsedData.scheduled_for) {
          parsedData.scheduled_for = new Date(parsedData.scheduled_for);
        }
        
        if (parsedData.published_at) {
          parsedData.published_at = new Date(parsedData.published_at);
        }
        
        // Ensure metadata.sources is an array
        if (parsedData.metadata && !Array.isArray(parsedData.metadata.sources)) {
          parsedData.metadata.sources = [];
        }
        
        console.log("Loaded auto-saved data from localStorage", parsedData);
        toast.info("Loaded auto-saved draft");
        return parsedData;
      }
    } catch (error) {
      console.error("Error loading saved draft:", error);
    }
    return null;
  }, [id, getLocalStorageKey]);

  // Set up auto-save timer
  useEffect(() => {
    if (hasUnsavedChanges) {
      // Clear any existing timer
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
      
      // Set up new timer for auto-save
      autoSaveTimerRef.current = setInterval(() => {
        saveToLocalStorage();
      }, AUTO_SAVE_INTERVAL);
    }
    
    // Clean up timer on unmount
    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [hasUnsavedChanges, formData, saveToLocalStorage]);

  return {
    saveToLocalStorage,
    clearLocalStorage,
    loadFromLocalStorage
  };
};
